import React, { useEffect, useState } from "react";
import { Button, Input, Select } from "@deeptrue-frontend/ui";
import { Formik, Form, Field as FormikField, FormikHelpers } from "formik";
import styled from "styled-components/macro";
import * as Yup from "yup";
import { FormatMessage } from "components/IntlProvider/hooks/hooks";
import { useFormatMessage } from "components/IntlProvider";
import Search, { OptionType } from "components/Fields/Search";
import api from "api";
import { GroupType } from "pages/OrganizationAdmin/types";
import InfoIcon from "@material-ui/icons/Info";
import Tooltip from "@material-ui/core/Tooltip";

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
`;

const FormStyled = styled(Form)`
  margin-top: 2rem;
  width: 35rem;
`;

const Ul = styled.ul`
  padding: 0 0.5rem;
  list-style-type: none;
`;

const Li = styled.li`
  margin-bottom: 0.5rem;
`;

export type FormValues = {
  email: string;
  organization_role: string;
  org_groups?: OptionType[];
};

interface Props {
  onSubmit: (values: FormValues, helpers: FormikHelpers<FormValues>) => void;
  closeModal: () => void;
  organizationCodename: string;
}

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<any>({
    email: Yup.string()
      .email(t("errors.fields.email.format", "Invalid email format"))
      .required(t("errors.fields.required", "Field is required")),
  });

const InviteUser = ({ onSubmit, closeModal, organizationCodename }: Props) => {
  const [groups, setGroups] = useState([]);
  const t = useFormatMessage();

  const getGroups = (searchValue?: string) => {
    return api.resources.organization.groups
      .getGroups({
        organizationCodename: organizationCodename,
        params: {
          search: searchValue,
        },
      })
      .then(({ data }) => {
        setGroups(
          data.results.map((group: GroupType) => ({
            value: group.id,
            label: group.name,
          }))
        );
      });
  };

  useEffect(() => {
    getGroups();
    // eslint-disable-next-line
  }, []);

  return (
    <Formik
      initialValues={{
        email: "",
        organization_role: "1",
        org_groups: [],
      }}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(t)}
    >
      {({ handleSubmit, touched, errors, setFieldValue, values }) => {
        return (
          <FormStyled onSubmit={handleSubmit}>
            <FormikField
              name="email"
              as={Input}
              hasError={touched.email && errors.email}
              helperText={touched.email && errors.email}
              label={"Email"}
            />
            <FormikField
              as={Select}
              label={
                <TooltipWrapper>
                  {t("organization.columns.role", "Role")}
                  <Tooltip
                    title={
                      <Ul>
                        <Li>
                          {t(
                            "organization.roles.admin",
                            "Admin User - the only role that is able to see 'Admin' panel on the left navigation bar. This means that this is the only user role that can access admin features such as - inviting new users to the organization, managing users, creating and managing user groups, sharing projects with users and groups, and sharing products with users and groups. Admin user naturally has access to all projects and products within the organization."
                          )}
                        </Li>
                        <Li>
                          {t(
                            "organization.roles.power",
                            "Power User - this user has access to all projects and products within the organization"
                          )}
                        </Li>
                        <Li>
                          {t(
                            "organization.roles.member",
                            "Member User - this user has access to projects he created and the ones shared with him or the group he is a part of. Moreover, according to the 'minimal access policy' this user can create projects using only products that have been shared with him or the group he is a part of (by default Member User doesn't have access to any projects and products)."
                          )}
                        </Li>
                        <Li>
                          {t(
                            "organization.roles.guest",
                            "Guest User - this user can only access the projects shared with him or the group he is a part of, but he cannot create projects by himself"
                          )}
                        </Li>
                      </Ul>
                    }
                  >
                    <InfoIcon style={{ marginLeft: "0.5rem" }} />
                  </Tooltip>
                </TooltipWrapper>
              }
              name="organization_role"
              hasError={touched.organization_role && errors.organization_role}
              helperText={touched.organization_role && errors.organization_role}
              onChange={setFieldValue}
              options={[
                {
                  value: "1",
                  label: "Admin User",
                },
                {
                  value: "2",
                  label: "Member User",
                },
                {
                  value: "3",
                  label: "Power User",
                },
                {
                  value: "4",
                  label: "Guest User",
                },
              ]}
            />
            <Search
              name="org_groups"
              options={groups}
              multiple={true}
              label={t("organization.columns.groups", "Groups")}
              hasError={!!(touched.org_groups && errors.org_groups)}
              helperText={touched.org_groups && errors.org_groups}
              asyncSeach={(searchedValue: string) => getGroups(searchedValue)}
              onChange={setFieldValue}
              value={values.org_groups || []}
            />

            <ButtonWrapper>
              <Button
                style={{ textTransform: "capitalize" }}
                type="button"
                variant="outlined"
                onClick={closeModal}
              >
                {t("pages.project.modal.cancel", "Cancel")}
              </Button>
              <Button ml="1rem" type="submit" variant="primary">
                {t("dashboard.respondendts.send", "Send")}
              </Button>
            </ButtonWrapper>
          </FormStyled>
        );
      }}
    </Formik>
  );
};

export default InviteUser;
